import React, { useRef, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { MessageOutlined } from "@ant-design/icons";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Area,
  AreaChart,
  Tooltip,
} from "recharts";
import {
  BuildingIcon,
  FormIcon,
  HeadphoneIcon,
  VideoIcon,
  UsersGroupIcon,
} from "../../assets/svgs/icons";
import {
  CircularProgress,
  SemiCircleProgress,
} from "../../components/charts/ProgressCharts";
import {
  useGetAssignmentStatsQuery,
  useGetCampaignsQuery,
  useGetCampaignStatsQuery,
  useGetDashboardStatsQuery,
  useGetDashboardYearlyStatsQuery,
  useGetFormsQuery,
  useGetInterpretersQuery,
  useGetInterpreterStatsQuery,
  useGetOrganizationsQuery,
  useGetOrganizationStatsQuery,
  useGetSupportTicketsQuery,
} from "../../store/api/apiSlice";
import DashboardHeader from "../../components/layout/PageHeader/DashboardHeader";
import FilterBar from "../../components/FilterBar";
import {
  buildDashboardQueryParams,
  getCampaignsPageStats,
  getDashboardStatsView,
  getFormsPageStats,
  getOrganizationsPageStats,
  mapYearlyDashboardData,
  overlayDashboardWithPageStats,
} from "../../utils/dashboardStats";
import {
  calculateInterpreterStats,
  getAssignmentOverviewStats,
} from "./Interpreter/helpers";

const Dashboard = ({ collapsed, handleToggle }) => {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState("lifetime");
  const [dateRange, setDateRange] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [selectedOrgs, setSelectedOrgs] = useState([]); // Change to array
  const [selectedCampaigns, setSelectedCampaigns] = useState([]); // Change to array
  const [selectedForms, setSelectedForms] = useState([]); // Change to array
  // const [selectedForms, setSelectedForms] = useState([]);

  const dashboardRef = useRef();

  // Queries with loading states
  const { data: organizations, isLoading: isOrgsLoading } =
    useGetOrganizationsQuery();
  const { data: campaigns, isLoading: isCampaignsLoading } =
    useGetCampaignsQuery();
  const { data: forms, isLoading: isFormsLoading } = useGetFormsQuery();
  const { data: supportTickets, isLoading: isSupportLoading } =
    useGetSupportTicketsQuery({ archived: false });
  const { data: organizationStats } = useGetOrganizationStatsQuery();
  const { data: interpreters } = useGetInterpretersQuery({ search: "" });
  const { data: interpreterStats } = useGetInterpreterStatsQuery();
  const { data: campaignStats } = useGetCampaignStatsQuery();
  const { data: assignmentStats } = useGetAssignmentStatsQuery();

  // Format date to Unix timestamp (seconds since epoch)
  const formatDateToUnix = (date) => {
    if (!date) return null;
    return Math.floor(date.getTime() / 1000);
  };

  // Calculate date range based on selected period (memoized to prevent recalculation)
  const calculateDateRange = (periodValue) => {
    if (!periodValue || periodValue === "lifetime") {
      return { startDate: null, endDate: formatDateToUnix(new Date()) };
    }

    const today = new Date();
    let startDate = null;

    switch (periodValue) {
      case "7days":
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        startDate = formatDateToUnix(sevenDaysAgo);
        break;
      case "30days":
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        startDate = formatDateToUnix(thirtyDaysAgo);
        break;
      case "year":
        const oneYearAgo = new Date(today);
        oneYearAgo.setFullYear(today.getFullYear() - 1);
        startDate = formatDateToUnix(oneYearAgo);
        break;
      default:
        startDate = null;
    }

    return { startDate, endDate: formatDateToUnix(today) };
  };

  // Memoize the date calculations to prevent unnecessary recalculations
  const dateParams = useMemo(() => {
    // If custom date range is selected
    if (dateRange && dateRange[0] && dateRange[1]) {
      return {
        startDate: Math.floor(dateRange[0].valueOf() / 1000),
        endDate: Math.floor(dateRange[1].valueOf() / 1000),
      };
    }

    // Otherwise use calculated period
    return calculateDateRange(selectedPeriod);
  }, [selectedPeriod, dateRange]);

  // Memoize the query parameters to prevent unnecessary API calls
  const queryParams = useMemo(
    () =>
      buildDashboardQueryParams({
        selectedPeriod,
        dateParams,
        dateRange,
        selectedOrgs,
        selectedCampaigns,
        selectedForms,
      }),
    [
      selectedPeriod,
      dateParams,
      dateRange,
      selectedOrgs,
      selectedCampaigns,
      selectedForms,
    ],
  );

  // Fetch dashboard stats with memoized parameters
  const {
    data: statsData,
    isLoading: isStatsLoading,
    isFetching: isStatsFetching,
  } = useGetDashboardStatsQuery(queryParams);

  const {
    data: yearlyStatsData,
    isLoading: isYearlyStatsLoading,
    isFetching: isYearlyStatsFetching,
  } = useGetDashboardYearlyStatsQuery(queryParams);

  // Combined loading state for filter operations
  const isFilterLoading = isOrgsLoading || isCampaignsLoading || isFormsLoading;

  // Set filtering state when user changes filters
  useEffect(() => {
    setIsFiltering(isStatsFetching || isYearlyStatsFetching);
  }, [isStatsFetching, isYearlyStatsFetching]);

  const handlePeriodChange = (value) => {
    setSelectedPeriod(value);
    // Clear custom date range when switching to preset periods
    setDateRange(null);
  };

  // Transform the API response data for chart
  const responseData = useMemo(
    () => mapYearlyDashboardData(yearlyStatsData),
    [yearlyStatsData],
  );

  const stats = useMemo(() => {
    const dashboardView = getDashboardStatsView(statsData);
    const formsPageStats = getFormsPageStats(forms);
    const campaignsPageStats = getCampaignsPageStats(campaignStats);
    const organizationsPageStats = getOrganizationsPageStats(organizationStats);
    const interpreterPageStats = calculateInterpreterStats(
      interpreters,
      interpreterStats,
    );
    const assignmentCards = getAssignmentOverviewStats(assignmentStats);
    const assignmentByLabel = Object.fromEntries(
      assignmentCards.map((card) => [card.label, card.value]),
    );

    return overlayDashboardWithPageStats(dashboardView, {
      totalUsers: organizationsPageStats.totalUsers,
      totalOrganizations: organizationsPageStats.totalOrganizations,
      ...formsPageStats,
      ...campaignsPageStats,
      totalInterpreters: interpreterPageStats.total,
      completedAssignments: interpreterPageStats.completed,
      incompleteAssignments: interpreterPageStats.incomplete,
      videosUnassigned: assignmentByLabel.Unassigned,
      assignedAssignments: assignmentByLabel.Assigned,
      untranslatedVideos:
        dashboardView.untranslatedVideos || assignmentByLabel.Unassigned,
      totalAssignments: assignmentByLabel["Total Videos"],
    });
  }, [
    statsData,
    forms,
    campaignStats,
    organizationStats,
    interpreters,
    interpreterStats,
    assignmentStats,
  ]);

  const supportEnquiryCounts = useMemo(() => {
    const tickets = supportTickets?.data || [];

    return tickets.reduce(
      (counts, ticket) => {
        const status = String(ticket.status || "")
          .toLowerCase()
          .replace(/\s+/g, "_");

        if (status === "open") {
          counts.open += 1;
        } else if (status === "in_progress") {
          counts.inProgress += 1;
        } else if (status === "resolved") {
          counts.resolved += 1;
        }

        return counts;
      },
      { open: 0, inProgress: 0, resolved: 0 },
    );
  }, [supportTickets]);

  const StatCard = ({
    icon,
    value,
    label,
    textColor = "text-gray-900",
    isLoading,
  }) => (
    <div className="bg-secondary rounded-xl p-6">
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 flex justify-center items-center">
            {icon}
          </span>
          <div className={`text-2xl font-semibold ${textColor}`}>
            {isLoading ? "" : value}
          </div>
        </div>
        <div className="text-gray-500 text-sm">{label}</div>
      </div>
    </div>
  );

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    setExportModalVisible(true);
  };

  const goToSupportEnquiries = (status) => {
    const params = new URLSearchParams({ tab: "support" });

    if (status) {
      params.set("status", status);
    }

    navigate(`/admin/enquiries?${params.toString()}`);
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    if (dates && dates.length === 2) {
      // Clear period selection when custom date is selected
      setSelectedPeriod(null);
    }
  };
  const handleOrgChange = (values) => {
    setSelectedOrgs(values);
    setSelectedCampaign(null); // Reset dependent filters
    setSelectedForm(null);
  };

  const handleCampaignChange = (values) => {
    setSelectedCampaigns(values);
    setSelectedForms([]); // Reset dependent filters if needed
  };

  const handleFormChange = (values) => {
    setSelectedForms(values); // CORRECT: Use setSelectedForms instead of setSelectedForm
  };

  // Loading overlay component
  const LoadingOverlay = ({ visible }) => {
    if (!visible) return null;

    return (
      <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10 rounded-xl">
        <Spin size="large" tip="Loading data...">
          <div className="w-32 h-10" />
        </Spin>
      </div>
    );
  };

  return (
    <div id="printable-dashboard" ref={dashboardRef} className="relative">
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            #printable-dashboard,
            #printable-dashboard * {
              visibility: visible;
            }
            #printable-dashboard {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            .no-print {
              display: none !important;
            }
            .page-break {
              page-break-before: always;
            }
            /* Ensure charts are visible */
            .recharts-surface {
              width: 100% !important;
            }
            /* Adjust grid layout for print */
            .grid {
              display: grid !important;
              grid-template-columns: repeat(5, 1fr) !important;
            }
          }
        `}
      </style>

      <DashboardHeader
        selectedPeriod={selectedPeriod}
        onPeriodChange={handlePeriodChange}
        collapsed={collapsed}
        handleToggle={handleToggle}
        showToggle={true}
        onExport={handleExport}
        onPrint={handlePrint}
        disabled={isFiltering}
      />

      <FilterBar
        onDateChange={handleDateChange}
        onOrgChange={handleOrgChange}
        onCampaignChange={handleCampaignChange}
        onFormChange={handleFormChange} // This should now update selectedForms correctly
        organizations={organizations?.data || []}
        campaigns={campaigns?.data || []}
        forms={forms?.data || []}
        isLoading={isFilterLoading || isFiltering}
      />

      <div className="flex justify-between items-center mb-2 selectSecondary mt-3">
        <h3 className="text-md font-semibold mb-0">Analytics Overview</h3>
      </div>
      <LoadingOverlay visible={isFiltering} />

      {/* Rest of the component remains the same */}
      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 relative">
        <StatCard
          icon={<UsersGroupIcon className="w-6 h-6 text-gray-600" />}
          value={stats.totalUsers}
          label="Total Users"
          isLoading={isStatsLoading || isFiltering}
        />
        <StatCard
          icon={<HeadphoneIcon className="w-6 h-6 text-gray-600" />}
          value={stats.totalInterpreters}
          label="Total Interpreters"
          isLoading={isStatsLoading || isFiltering}
        />
        <StatCard
          icon={<FormIcon className="" width={14} height={14} />}
          value={stats.totalForms}
          label="Total Forms"
          isLoading={isStatsLoading || isFiltering}
        />
        <StatCard
          icon={<UsersGroupIcon className="w-6 h-6 text-gray-600" />}
          value={stats.totalCampaigns}
          label="Total Campaigns"
          textColor="text-orange"
          isLoading={isStatsLoading || isFiltering}
        />
        <StatCard
          icon={<BuildingIcon className="w-6 h-6 text-gray-600" />}
          value={stats.totalOrganizations}
          label="Total Organisations"
          isLoading={isStatsLoading || isFiltering}
        />
        <StatCard
          icon={<VideoIcon className="w-6 h-6 text-gray-600" />}
          value={stats.untranslatedVideos}
          label="Untranslated Videos"
          textColor="text-red-500"
          isLoading={isStatsLoading || isFiltering}
        />
        <div className="bg-secondary rounded-xl p-6">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 flex justify-center items-center">
                <MessageOutlined
                  className="w-6 h-6 text-gray-600"
                  style={{ transform: "scale(1.5)" }}
                />
              </span>
              <div className="text-2xl font-semibold text-gray-900">
                {isSupportLoading || isFiltering ? (
                  ""
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => goToSupportEnquiries("open")}
                      className="border-0 bg-transparent p-0 text-2xl font-semibold text-red-500 cursor-pointer hover:underline"
                    >
                      {supportEnquiryCounts.open}
                    </button>
                    <span> - </span>
                    <button
                      type="button"
                      onClick={() => goToSupportEnquiries("in_progress")}
                      className="border-0 bg-transparent p-0 text-2xl font-semibold text-orange cursor-pointer hover:underline"
                    >
                      {supportEnquiryCounts.inProgress}
                    </button>
                    <span> - </span>
                    <button
                      type="button"
                      onClick={() => goToSupportEnquiries("resolved")}
                      className="border-0 bg-transparent p-0 text-2xl font-semibold text-[#10B981] cursor-pointer hover:underline"
                    >
                      {supportEnquiryCounts.resolved}
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="text-gray-500 text-sm">Support Enquiries</div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mt-2">
        <div>
          <div className="bg-secondary rounded-xl p-2 relative">
            <div className="flex justify-between items-center mb-3 selectSecondary">
              <h3 className="text-md font-semibold">Form Performance</h3>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="bg-white rounded-xl p-2 mb-2">
                  <div className="text-2xl font-semibold">
                    {isStatsLoading || isFiltering
                      ? ""
                      : stats.activeForms}
                  </div>
                  <div className="text-gray-500 text-sm">
                    Total Active Forms
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4">
                  <div className="text-2xl font-semibold">
                    {isStatsLoading || isFiltering ? (
                      ""
                    ) : (
                      <>{stats.responseRateLabel}</>
                    )}
                  </div>
                  <div className="text-gray-500 text-sm">Response Rate</div>
                </div>
              </div>
              <SemiCircleProgress
                isLoading={isStatsLoading || isFiltering}
                statsData={stats}
              />
            </div>
          </div>

          <div className="bg-secondary rounded-xl p-2 mt-2 relative">
            <div className="flex justify-between items-center mb-6 selectSecondary">
              <h3 className="text-md font-semibold m-0">Response Metrics</h3>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 mt-0 gap-2">
              <div className="h-[300px] col-span-2">
                {isYearlyStatsLoading || isFiltering ? (
                  <div className="h-full flex items-center justify-center">
                    <Spin size="large" />
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart
                      data={responseData}
                      margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
                    >
                      <defs>
                        <linearGradient
                          id="colorTotal"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#EF4444"
                            stopOpacity={0.2}
                          />
                          <stop
                            offset="95%"
                            stopColor="#EF4444"
                            stopOpacity={0}
                          />
                        </linearGradient>
                        <linearGradient
                          id="colorVideo"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#3B82F6"
                            stopOpacity={0.2}
                          />
                          <stop
                            offset="95%"
                            stopColor="#3B82F6"
                            stopOpacity={0}
                          />
                        </linearGradient>
                        <linearGradient
                          id="colorText"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#10B981"
                            stopOpacity={0.2}
                          />
                          <stop
                            offset="95%"
                            stopColor="#10B981"
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        vertical={false}
                        stroke="#f0f0f0"
                        strokeDasharray="3 3"
                      />
                      <XAxis
                        dataKey="year"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#6B7280", fontSize: 12 }}
                        dy={10}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#6B7280", fontSize: 12 }}
                        dx={-10}
                      />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="total"
                        stroke="#EF4444"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorTotal)"
                      />
                      <Area
                        type="monotone"
                        dataKey="video"
                        stroke="#3B82F6"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorVideo)"
                      />
                      <Area
                        type="monotone"
                        dataKey="text"
                        stroke="#10B981"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorText)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div className="col-span-1">
                <div className="bg-white mb-2 p-2 rounded-xl">
                  <div className="text-[#EF4444] text-2xl font-semibold">
                    {isStatsLoading || isFiltering
                      ? ""
                      : stats.totalResponses}
                  </div>
                  <div className="text-gray-500 text-sm">Total Responses</div>
                </div>
                <div className="bg-white mb-2 p-2 rounded-xl">
                  <div className="text-[#3B82F6] text-2xl font-semibold">
                    {isStatsLoading || isFiltering
                      ? ""
                      : stats.videoResponses}
                  </div>
                  <div className="text-gray-500 text-sm">
                    Total Video Responses
                  </div>
                </div>
                <div className="bg-white mb-2 p-2 rounded-xl">
                  <div className="text-[#10B981] text-2xl font-semibold">
                    {isStatsLoading || isFiltering
                      ? ""
                      : stats.textResponses}
                  </div>
                  <div className="text-gray-500 text-sm">
                    Total Text Responses
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="bg-secondary rounded-xl p-2 relative">
            <div className="flex justify-between items-center mb-6 selectSecondary">
              <h3 className="text-md font-semibold">Interpreter Activity</h3>
            </div>
            <div className="grid md:grid-cols-2 grid-cols-1 gap-2 mt-2">
              <div className="w-full">
                <CircularProgress
                  data={stats}
                  isLoading={isStatsLoading || isFiltering}
                />
              </div>

              <div className="grid grid-cols-1 gap-2">
                <div className="bg-white rounded-xl p-4">
                  <div className="text-2xl font-semibold text-[#F97316]">
                    {isStatsLoading || isFiltering
                      ? ""
                      : stats.videosUnassigned}
                  </div>
                  <div className="text-gray-500 text-sm">Videos Unassigned</div>
                </div>
                <div className="bg-white rounded-xl p-4">
                  <div className="text-2xl font-semibold text-[#EF4444]">
                    {isStatsLoading || isFiltering
                      ? ""
                      : stats.assignedAssignments}
                  </div>
                  <div className="text-gray-500 text-sm">
                    Videos Being Translated
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4">
                  <div className="text-2xl font-semibold text-[#3B82F6]">
                    {isStatsLoading || isFiltering
                      ? ""
                      : stats.secondCheckAssignment}
                  </div>
                  <div className="text-gray-500 text-sm">
                    Second Check Assigned
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-secondary rounded-xl p-2 mt-2 relative">
            <div className="flex justify-between items-center mb-6 selectSecondary">
              <h3 className="text-md font-semibold">Campaign Engagement</h3>
            </div>
            <div className="grid md:grid-cols-2 grid-cols-1 gap-4 mt-4">
              <div className="bg-white rounded-xl p-4">
                <div className="text-2xl font-semibold text-blue">
                  {isStatsLoading || isFiltering
                    ? ""
                    : stats.activeCampaigns}
                </div>
                <div className="text-gray-500 text-sm">
                  Total Active Campaigns
                </div>
              </div>
              <div className="bg-white rounded-xl p-4">
                <div className="text-2xl font-semibold text-orange">
                  {isStatsLoading || isFiltering
                    ? ""
                    : stats.totalParticipants}
                </div>
                <div className="text-gray-500 text-sm">
                  Number of Participations
                </div>
              </div>

              {/* <div className="w-full">
              <CampaignProgress 
  data={statsData?.data}
  isLoading={isStatsLoading || isFiltering} 
/>              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
